/* eslint-disable react/jsx-props-no-spreading */
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useLocation } from 'react-router';

import Button from '../buttons/Button';
import ChevronIcon from '../icons/Chevron';
import Accordion from '../partials/Accordion';

import styles from '../../../styles/filters/options.module.scss';

const propTypes = {
    onChange: PropTypes.func,
    value: PropTypes.any, // eslint-disable-line react/forbid-prop-types
    filter: PropTypes.shape({
        type: PropTypes.string,
        id: PropTypes.string,
        name: PropTypes.string,
    }),
    className: PropTypes.string,
    isOpenClassName: PropTypes.string,
    itemWrapperClassName: PropTypes.string,
};

const defaultProps = {
    onChange: null,
    value: null,
    filter: null,
    className: null,
    isOpenClassName: null,
    itemWrapperClassName: null,
};

const OptionsFilter = ({
    onChange,
    value,
    filter,
    className,
    isOpenClassName,
    itemWrapperClassName,
}) => {
    const {
        options = [],
        multiple = false,
        baseUrl = null,
        accordion = true,
        label: filterLabel = null,
    } = filter || {};

    const { pathname = null } = useLocation();

    const currentValue = multiple ? value || [] : value || null;

    const currentOptions = options.filter(
        ({ value: optionValue }) =>
            (multiple && currentValue.indexOf(optionValue) !== -1) || optionValue === currentValue,
    );

    const onOptionSelect = useCallback(
        (e, newValue) => {
            e.preventDefault();
            if (!multiple) {
                onChange(!currentValue || currentValue !== newValue ? newValue : null);
                return;
            }
            const newValues = [...currentValue];
            if (!currentValue.includes(newValue)) {
                newValues.push(newValue);
                onChange(newValues);
            } else {
                const filteredOptions = newValues.filter((op) => op !== newValue);
                onChange(filteredOptions);
            }
        },
        [onChange, filter, currentValue, multiple],
    );

    const inner = (
        <div
            className={classNames([
                styles.container,
                {
                    [className]: className !== null && !accordion,
                },
            ])}
        >
            {(options || []).map((op, i) => {
                const { label = null, value: optionValue = null, url = null } = op || {};
                if (label === null || optionValue === null) {
                    return null;
                }
                const optionSelected = multiple
                    ? currentValue.includes(optionValue) === true
                    : currentValue === optionValue;
                const isUrl = url !== null && url === pathname;
                const selected = optionSelected || isUrl;
                const currentUrl = selected ? baseUrl || url : url;
                const isLink = url !== null;

                return (
                    <Button
                        key={`option-${label}-${i + 1}-${url}`}
                        href={currentUrl}
                        className={classNames([
                            styles.option,
                            {
                                [styles.isLink]: isLink,
                                [styles.selected]: selected,
                            },
                        ])}
                        onClick={url === null ? (e) => onOptionSelect(e, optionValue) : null}
                    >
                        {label}
                    </Button>
                );
            })}
        </div>
    );

    return accordion ? (
        <Accordion
            label={
                <>
                    {filterLabel}
                    {currentOptions.length > 0 && multiple ? (
                        <div className={styles.activeFiltersBadge}>{currentOptions.length}</div>
                    ) : null}
                    <ChevronIcon />
                </>
            }
            className={classNames([
                styles.accordion,
                {
                    [className]: className !== null,
                    [styles.hasSelected]: currentOptions.length > 0,
                    [styles.disabled]: options === null || options.length === 0,
                },
            ])}
            isOpenClassName={classNames([
                styles.isOpen,
                {
                    [isOpenClassName]: isOpenClassName !== null,
                },
            ])}
            titleClassName={styles.label}
            toggleClassName={styles.toggle}
            toggleButtonClassName={styles.toggleButton}
            itemWrapperClassName={classNames([
                styles.wrapper,
                {
                    [itemWrapperClassName]: itemWrapperClassName !== null,
                },
            ])}
            contentClassName={styles.content}
        >
            {inner}
        </Accordion>
    ) : (
        inner
    );
};

OptionsFilter.propTypes = propTypes;
OptionsFilter.defaultProps = defaultProps;

export default OptionsFilter;
